import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Pencil } from 'lucide-react';
import { Loading, Badge, Button } from '@/components/ui';
import EditPartnerModal from '@/components/affiliate/EditPartnerModal';
import { affiliateApi } from '@/api/affiliate';
import { orgsApi } from '@/api/orgs';
import AgreementsTab from './tabs/AgreementsTab';
import SignupLinksTab from './tabs/SignupLinksTab';
import CustomersTab from './tabs/CustomersTab';
import EarningsTab from './tabs/EarningsTab';

type TabKey = 'agreements' | 'links' | 'customers' | 'earnings';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'agreements', label: 'Agreements' },
  { key: 'links', label: 'Signup links' },
  { key: 'customers', label: 'Customers' },
  { key: 'earnings', label: 'Earnings' },
];

export default function PartnerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [tab, setTab] = useState<TabKey>('agreements');
  const [editOpen, setEditOpen] = useState(false);

  const { data: partner, isLoading, error } = useQuery({
    queryKey: ['affiliate-partner', id],
    queryFn: () => affiliateApi.partners.get(id!),
    enabled: !!id,
  });

  const { data: linkedOrg } = useQuery({
    queryKey: ['affiliate-partner-linked-org', partner?.linked_org_id],
    queryFn: () => orgsApi.get(partner!.linked_org_id!),
    enabled: !!partner?.linked_org_id,
  });

  if (!id) return <div className="p-6 text-danger text-sm">Missing partner id</div>;
  if (isLoading) return <div className="p-6"><Loading text="Loading partner…" /></div>;
  if (error || !partner) {
    return (
      <div className="p-6 space-y-4">
        <BackLink />
        <p className="text-danger text-sm">Partner not found</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5">
      <BackLink />

      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold text-white">{partner.name}</h1>
        <Badge variant={partner.is_active ? 'success' : 'neutral'}>
          {partner.is_active ? 'active' : 'inactive'}
        </Badge>
        <Button size="sm" variant="ghost" onClick={() => setEditOpen(true)} className="ms-auto">
          <Pencil size={14} className="mr-1" /> Edit partner
        </Button>
      </div>

      <div className="card p-4">
        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <Detail label="Email" value={partner.contact_email ?? '—'} />
          <Detail label="Phone" value={partner.contact_phone ?? '—'} />
          <Detail
            label="JumpStart organization"
            value={
              partner.linked_org_id ? (
                linkedOrg ? (
                  <span>
                    {linkedOrg.name}
                    <span className="text-ink-muted text-xs ms-1">
                      ({linkedOrg.slug || `${linkedOrg.id.slice(0, 8)}…`})
                    </span>
                  </span>
                ) : (
                  <span className="font-mono text-xs">{partner.linked_org_id}</span>
                )
              ) : (
                '—'
              )
            }
          />
          <Detail
            label="WA partner link"
            value={partner.ext_partner_id ? <span className="font-mono text-xs">{partner.ext_partner_id}</span> : '—'}
          />
        </dl>
      </div>

      <div className="flex gap-1 border-b border-navy-border">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
              tab === t.key
                ? 'border-gold text-white'
                : 'border-transparent text-ink-muted hover:text-ink-secondary'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div>
        {tab === 'agreements' && <AgreementsTab partner={partner} />}
        {tab === 'links' && <SignupLinksTab partnerId={partner.id} />}
        {tab === 'customers' && <CustomersTab partnerId={partner.id} />}
        {tab === 'earnings' && <EarningsTab partnerId={partner.id} />}
      </div>

      <EditPartnerModal open={editOpen} partner={partner} onClose={() => setEditOpen(false)} />
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/affiliate/partners"
      className="inline-flex items-center gap-2 text-sm text-indigo-300 hover:text-indigo-200"
    >
      <ArrowLeft size={16} /> Back to partners
    </Link>
  );
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-ink-muted text-xs">{label}</dt>
      <dd className="text-ink-primary mt-0.5">{value}</dd>
    </div>
  );
}
